import { findWhere } from "underscore";
import { useParams } from "react-router-dom";

import TableLayoutsButton, {
  DropdownButton,
  layoutTypeMap,
} from "../../../TableLayouts/TableLayoutsButton";
import useUpdateView from "../../../../api/api-hooks/views/useUpdateView";
import usePageViewTable from "../usePageViewTable";
import useCloudPermissions from "../../../../hooks/useCloudPermissions";
import useWorkspacePermissions from "../../../../hooks/useWorkspacePermissions";
import usePagePermissions from "../../../../hooks/usePagePermissions";
import { Module } from "../../../../types/TenantInterfaces";
import { newToastEvent } from "../../../../utils/ToastUtil";
import { handleSaveGlobalLayout } from "../../../../utils/LayoutUtils";
import { Layout } from "../../../../types/GlobalInterfaces";
import useCreateGlobalLayout from "../../../../api/api-hooks/globals/useCreateGlobal";
import { GetTableGroupBy } from "../../../../utils/QueryUtils";
import { GroupedLayout, View } from "../../../../types/PageInterfaces";
import { SharedViewModes } from "../../PageViewRenderer";
import { Popover, Whisper } from "rsuite";
import { ButtonGroup } from "reactstrap";
import { useRef, useState } from "react";
import SharedModeButton from "./SharedModeButton";
import DropdownMenuDivider from "../../../Menus/DropdownMenuDivider";
import { useNavigate } from "react-router-dom";
import { CalendarConfig } from "../../AddViewDrawer/AddViewDrawer";
import { useAtomValue } from "jotai";
import { IsMobileAtom } from "../../../../App";

const layoutsPropMap = {
  Table: "TableLayouts",
  Cards: "CardsLayouts",
  Board: "BoardLayouts",
  Timeline: "TimelineLayouts",
  Calendar: "CalendarConfig",
} as const;

// type LayoutProps = typeof layoutsPropMap[keyof typeof layoutsPropMap];

export interface UpdateTLayout {
  Fields: string[];
  GetAllGrouped: boolean;
  GroupBy: { Field: string } | null;
}

interface Props {
  foundLayout: any;
  query: any;
  selectedLayout: any;
  setQuery: (newQuery: any) => void;
  currentModule: Module;
  updateQuery: (newQuery: any) => void;
  setSelectedLayout: (id: string) => void;
  updateLayout: ({ Fields, GetAllGrouped, GroupBy }: UpdateTLayout) => void;
  viewMode: SharedViewModes;
  view: View;
  parentViewID?: string;
  setSelected: (selected: object) => void;
  setViewMode: (newMode: SharedViewModes) => void;
}

/**
 * SharedTableLayoutsButton - Adapts to Table, Cards, Board View.
 * Whatever the "viewMode" is - we grab the layouts from the ActiveView based on that.
 * `onSaveAsDefault` is only supported for the default view type (activeView.Type)
 */
const SharedTableLayoutsButton = ({
  query,
  foundLayout,
  selectedLayout,
  setQuery,
  currentModule,
  updateQuery,
  setSelectedLayout,
  updateLayout,
  viewMode,
  view,
  parentViewID,
  setSelected,
  setViewMode,
}: Props) => {
  const isMobile = useAtomValue(IsMobileAtom);
  const { pageID }: any = useParams();
  const { tenantID }: { tenantID: string } | any = useParams();
  const { updateView, viewUpdating } = useUpdateView(pageID);
  const navigate = useNavigate();
  const { createItem, updateItem, deleteItem, reorderItems } = usePageViewTable(
    {
      currentViewID: view?.id,
      updateView,
      parentViewID,
    }
  );
  const { cPermissions } = useCloudPermissions();
  const { wPermissions } = useWorkspacePermissions();
  const { pPermissions } = usePagePermissions(pageID);

  const layoutsProp = layoutsPropMap[(viewMode || "") as SharedViewModes] ?? "";
  const layouts = view?.[layoutsProp] ?? [];
  const isDefaultMode =
    isMobile && view?.MobileType
      ? viewMode === view.MobileType
      : viewMode === view?.Type;

  // Global Layouts
  const { createGlobal } = useCreateGlobalLayout();

  const [open, setOpen] = useState(false);
  const layoutInfo = layoutTypeMap[viewMode] || {};
  const whisperRef = useRef<any>();

  // @ts-ignore
  const LayoutComponents = TableLayoutsButton({
    layoutsType: viewMode,
    savedLayout: foundLayout?.Fields ?? view?.Query?.Fields,
    savedLayoutID: view?.Query?._SelectedLayout,
    selectedLayoutName: foundLayout?.Name ?? "Default Layout",
    selectedLayout: foundLayout?.Fields ?? query.Fields,
    selectedLayoutID: selectedLayout,
    layouts: layouts,
    calendarConfig: view?.CalendarConfig,
    onSaveCalendarConfig: onSaveCalendarConfig,
    onSaveAsDefault: isDefaultMode ? onSaveAsDefault : null,
    onSelect: onSelectLayout,
    onSwitchToDefault: onSwitchToDefaultLayout,
    createLayout: createItem,
    updateLayout: onUpdateLayout,
    submitting: viewUpdating,
    deleteLayout: deleteItem,
    reorderLayouts: reorderItems,
    currentModule: currentModule,
    validPermissions:
      cPermissions.cloud_modify_page ||
      wPermissions.workspace_modify_page ||
      pPermissions.page_modify_page,
    onSaveAsGlobal: handleSaveLayout,
    usePageSelection: true,
    // @ts-ignore
    handleAddGlobals: (newLayouts: any, onSuccess: any) => {
      // @ts-ignore
      updateView({
        viewID: view?.id as string,
        payload: {
          [layoutsProp]: newLayouts,
        },
        onSuccess,
        parentViewID,
      });
    },
    noWhisper: true,
    providedRef: whisperRef,
  });

  const ViewModeComponents = SharedModeButton({
    setSelected,
    view,
    parentViewID,
    viewMode,
    setViewMode,
    modeButtonRef: whisperRef,
  });

  return (
    <ButtonGroup className="ml-0">
      <Whisper
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        placement="autoVerticalEnd"
        trigger="click"
        ref={whisperRef}
        preventOverflow
        speaker={
          <Popover className="sidebar-popover" style={{ width: "350px" }} full>
            <div className="d-flex align-items-center pt-1 pb-0 bg-primary-selected justify-content-between rounded px-2 mb-2">
              <div className="d-flex align-items-center">
                <i
                  className={`mdi mdi-${layoutInfo?.icon} text-dark ${
                    view?.Type === viewMode ? "mr-1" : "mr-2"
                  }`}
                  style={{ fontSize: 20 }}
                />
                {view?.Type === viewMode && (
                  <i
                    className={`mdi mdi-star pr-1 text-warning`}
                    style={{ fontSize: 20 }}
                  />
                )}
                <h5 className="mb-0 text-dark flex-grow-1">
                  {layoutInfo.viewName}
                </h5>
              </div>
              <div>
                <i className="mdi mdi-chevron-down" style={{ fontSize: 24 }} />
              </div>
            </div>
            <div style={{ padding: "0px 10px" }}>
              {/* @ts-ignore */}
              {LayoutComponents.LayoutOptionsComponent}
            </div>
            {(!view?.HiddenViews || view?.HiddenViews?.length < 3) && (
              <DropdownMenuDivider />
            )}
            {ViewModeComponents?.ViewOptionsComponent}
          </Popover>
        }
        controlId="newRecordDropdown"
      >
        <div>
          <DropdownButton
            icon={layoutInfo?.icon}
            name={foundLayout?.Name ?? "Default Layout"}
            open={open}
          />
        </div>
      </Whisper>
      {/* @ts-ignore */}
      {LayoutComponents.Drawers}
      {ViewModeComponents?.Drawer}
    </ButtonGroup>
  );

  function onSaveAsDefault(id: string) {
    if (view?.Type === "Calendar") return;

    if (view?.Type !== viewMode) {
      return newToastEvent(
        "error",
        "Can't save as default if not the default view type."
      );
    }

    // We ensured the layouts will be a Layout[]
    // by ensuring this function isn't called when a calendar is the view.
    // (See line at top of function)
    const foundLayout = findWhere((layouts as Layout[]) ?? [], {
      id,
    });

    if (!foundLayout) return console.log("no findo");

    const newQuery = {
      ...query,
      ...GetTableGroupBy(foundLayout),
      Fields: foundLayout.Fields,
      _SelectedLayout: id,
    };

    setQuery(newQuery);
    updateQuery(newQuery);
  }

  function onSelectLayout(
    id: string,
    { Fields, GetAllGrouped, GroupBy }: UpdateTLayout
  ) {
    setSelectedLayout(id);
    updateLayout({ Fields, GetAllGrouped, GroupBy });
  }

  function onSwitchToDefaultLayout() {
    setSelectedLayout(view?.Query?._SelectedLayout ?? null);
    setQuery((prev: any) => ({
      ...prev,
      ...GetTableGroupBy(view?.Query),
      Fields: view?.Query?.Fields,
    }));
  }

  function onUpdateLayout(out: any) {
    if (!layoutsProp) {
      return newToastEvent("error", "DevError: Missing layouts property.");
    }

    const newLayouts = updateItem({
      ...out,
      dontCallAPI: true,
    });

    const newQuery = {
      ...query,
      ...GetTableGroupBy(out.payload),
      Fields: out.payload.Fields,
    };

    if (selectedLayout === out.id) {
      setQuery(newQuery);
    }

    // Update the Default Query (activeView.Query), if the out.id is the default,
    // Else: Only update the current selected layout.
    if (out?.id === view?.Query?._SelectedLayout) {
      // @ts-ignore
      updateView({
        viewID: view?.id as string,
        payload: {
          Query: newQuery,
          [layoutsProp]: newLayouts,
        },
        onSuccess: out.onSuccess,
        parentViewID,
      });
    } else {
      // @ts-ignore
      updateView({
        viewID: view?.id as string,
        payload: { [layoutsProp]: newLayouts },
        onSuccess: out.onSuccess,
        parentViewID,
      });
    }
  }

  function handleSaveLayout(layout: Layout) {
    if (!layoutsProp) {
      return newToastEvent("error", "DevError: Missing layouts property.");
    }

    if (!view?.ModuleAPIName)
      return newToastEvent("error", "DevError: Missing Module Name.");

    layout.ModuleAPIName = view?.ModuleAPIName;
    // @ts-ignore
    layout.Type = viewMode || "";

    handleSaveGlobalLayout({
      layout,
      navigate,
      tenantID,
      onSave: () => {
        createGlobal({
          global: layout,
          globalsType: "Layouts",
          onSuccess: saveAsGlobalLayout,
        });
      },
    });
  }

  function saveAsGlobalLayout(newGlobalLayout: Layout, previosLayout: Layout) {
    if (viewMode === "Calendar") return;

    const layouts = view?.[layoutsProp] as Layout[] | GroupedLayout[];

    if (!layouts || !layouts.length) return;

    const newLayouts = layouts.map((layout) => {
      if (layout.id === previosLayout.id) {
        return { id: previosLayout.id, GlobalID: newGlobalLayout.id };
      }

      return layout;
    });

    // @ts-ignore
    updateView({
      viewID: view?.id,
      payload: { [layoutsProp]: newLayouts },
      parentViewID,
    });
  }

  function onSaveCalendarConfig({
    config,
    onClose,
  }: {
    config: CalendarConfig;
    onClose: () => void;
  }) {
    // @ts-ignore
    updateView({
      viewID: view?.id,
      payload: { CalendarConfig: config },
      parentViewID,
      onSuccess: onClose,
    });
  }
};

export default SharedTableLayoutsButton;
